import { useEffect, useState, useCallback } from 'react';

/**
 * Tracks the most-recent dashboard/app paths a user visits and exposes
 * them for the profile menu's "Recent" section. Persisted to
 * localStorage so it survives reloads.
 *
 * Only tracks app-shell paths (`/dashboard`, `/messages`, `/account/*`,
 * `/saved`) — we don't pollute the list with marketing pages.
 */
const KEY = 'ccuk_recent_paths';
const MAX = 5;

const APP_PREFIXES = ['/dashboard', '/messages', '/account', '/saved', '/admin'];

function isAppPath(p) {
  return APP_PREFIXES.some((pre) => p === pre || p.startsWith(pre + '/') || p.startsWith(pre + '?'));
}

function read() {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function write(list) {
  try { localStorage.setItem(KEY, JSON.stringify(list)); } catch { /* quota */ }
}

export function recordRecentPath(pathWithSearch, label) {
  if (!pathWithSearch || !isAppPath(pathWithSearch.split('?')[0])) return;
  const list = read().filter((e) => e.path !== pathWithSearch);
  list.unshift({ path: pathWithSearch, label: label || pathWithSearch, ts: Date.now() });
  write(list.slice(0, MAX));
}

export function useRecentPaths() {
  const [recent, setRecent] = useState(read);
  // Keep this hook consumer in sync with localStorage changes from other
  // tabs / programmatic record calls.
  useEffect(() => {
    const onStorage = (e) => { if (e.key === KEY) setRecent(read()); };
    window.addEventListener('storage', onStorage);
    const interval = setInterval(() => setRecent(read()), 5000);
    return () => {
      window.removeEventListener('storage', onStorage);
      clearInterval(interval);
    };
  }, []);
  const clear = useCallback(() => { write([]); setRecent([]); }, []);
  return { recent, clear };
}
